import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Users, ArrowRight, Heart } from "lucide-react";
import api from "@/lib/api";
import { Logo } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";

export default function Collaborate() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | notfound | ready | needsauth | accepting
  const [accepted, setAccepted] = useState(null);

  useEffect(() => {
    if (!token) { setStatus("notfound"); return; }
    (async () => {
      try {
        const res = await api.get(`/collaborators/lookup?token=${encodeURIComponent(token)}`);
        setInvite(res.data);
        setStatus("ready");
      } catch { setStatus("notfound"); }
    })();
  }, [token]);

  const accept = async () => {
    if (!user) {
      // Save intent and route to signup with the token preserved.
      try { sessionStorage.setItem("gathervoices_pending_collab", token); } catch {}
      navigate(`/signup?next=${encodeURIComponent(`/collaborate?token=${token}`)}`);
      return;
    }
    setStatus("accepting");
    try {
      const res = await api.post("/collaborators/accept", { token });
      setAccepted(res.data);
      try { sessionStorage.removeItem("gathervoices_pending_collab"); } catch {}
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not accept — the link may have expired.";
      toast.error(typeof detail === "string" ? detail : "Could not accept");
      setStatus("ready");
    }
  };

  if (status === "loading") return (
    <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))]">
      <div className="h-10 w-10 rounded-full border-2 border-[hsl(var(--accent))] border-t-transparent animate-spin" />
    </div>
  );

  if (status === "notfound") return (
    <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))] px-4">
      <div className="max-w-md text-center">
        <Logo />
        <h1 className="mt-6 font-display text-2xl font-semibold">This link isn&apos;t valid anymore</h1>
        <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">The invitation may have been removed or already accepted. Ask the organizer to send a fresh one.</p>
        <button onClick={() => navigate("/")} className="mt-6 px-5 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-sm">Go home</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] grid place-items-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-6 sm:p-8">
        <Logo />
        {accepted ? (
          <div className="mt-6 text-center" data-testid="collaborate-accepted">
            <div className="mx-auto h-12 w-12 grid place-items-center rounded-full" style={{ background: "hsl(35 55% 92%)", color: "hsl(35 60% 30%)" }}>
              <Heart size={22} />
            </div>
            <h1 className="mt-4 font-display text-2xl font-semibold">You&apos;re in</h1>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
              You can now help moderate tributes and arrange the film for <strong>{invite?.honoree_name}</strong>.
            </p>
            <button
              data-testid="collaborate-open-event"
              onClick={() => navigate(`/events/${accepted.event_id}`)}
              className="mt-6 w-full h-11 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-sm inline-flex items-center justify-center gap-1.5"
            >
              Open the event <ArrowRight size={15} />
            </button>
          </div>
        ) : (
          <div className="mt-6" data-testid="collaborate-invite">
            <div className="flex items-center gap-2 text-xs font-semibold text-[hsl(var(--accent))] uppercase tracking-wide">
              <Users size={14} /> You&apos;ve been invited
            </div>
            <h1 className="mt-2 font-display text-2xl font-semibold">
              Help gather tributes for <span className="text-[hsl(var(--accent))]">{invite?.honoree_name}</span>
            </h1>
            <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
              {invite?.inviter_name || "The organizer"} asked you to co-organize this tribute on GatherVoices.
              You&apos;ll be able to review incoming tributes, arrange the film, and send invitations.
              Only the owner can delete the event.
            </p>
            <div className="mt-4 rounded-lg bg-[hsl(var(--muted))] px-3 py-2 text-[11.5px] text-[hsl(var(--muted-foreground))]">
              Invitation for <strong className="text-[hsl(var(--foreground))]">{invite?.invited_email}</strong>
              {user && user.email?.toLowerCase() !== (invite?.invited_email || "").toLowerCase() && (
                <div className="mt-1 text-[hsl(var(--destructive))]">
                  You&apos;re signed in as {user.email}. Please sign out and sign in with the invited address.
                </div>
              )}
            </div>
            <button
              data-testid="collaborate-accept"
              onClick={accept}
              disabled={status === "accepting" || (user && user.email?.toLowerCase() !== (invite?.invited_email || "").toLowerCase())}
              className="mt-5 w-full h-11 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
            >
              {user ? (status === "accepting" ? "Accepting…" : "Accept invitation") : "Sign up to accept"}
              <ArrowRight size={15} />
            </button>
            {!user && (
              <p className="mt-2 text-[11.5px] text-center text-[hsl(var(--muted-foreground))]">
                You&apos;ll be asked to create an account with <strong>{invite?.invited_email}</strong>.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
